import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Grid } from '@mui/material';

import { AccountBalanceV2, AccountId } from '@arrived/models';
import { useGetAccountBalanceQuery } from '@arrived/queries';

import { DataStateHandler } from '../../../DataStateHandler';
import { PortfolioCard } from './PortfolioCard';

import { PortfolioAppreciation, RentalIncomePercentText } from '../../common';
import { AppreciationDialog, RentalIncomeDialog } from '../dialogs';

export interface PortfolioCardsProps {
  accountId?: AccountId;
  className?: string;
}

export const PortfolioCards = ({ accountId, ...rest }: PortfolioCardsProps) => {
  const accountBalanceState = useGetAccountBalanceQuery(accountId);

  return (
    <DataStateHandler queries={[accountBalanceState]}>
      {(accountBalanceState) => (
        <PortfolioCardsContent accountBalance={accountBalanceState.data} accountId={accountId} {...rest} />
      )}
    </DataStateHandler>
  );
};

interface PortfolioCardsContentProps extends PortfolioCardsProps {
  accountBalance: AccountBalanceV2;
}

const PortfolioCardsContent = ({ accountBalance, accountId, className }: PortfolioCardsContentProps) => {
  const [isAppreciationDialogOpen, setIsAppreciationDialogOpen] = useState(false);
  const [isRentalIncomeDialogOpen, setIsRentalIncomeDialogOpen] = useState(false);

  const {
    totalValue,
    initialInvestment,
    appreciation: { totalAppreciation },
    rentalIncome: { blendedAnnualDividendPercent, totalDividendsPaid },
  } = accountBalance;

  const handleAppreciationOnClick = useCallback(() => {
    setIsAppreciationDialogOpen(true);
    setIsRentalIncomeDialogOpen(false);
  }, []);
  const handleAppreciationDialogOnClose = useCallback(() => setIsAppreciationDialogOpen(false), []);

  const handleRentalIncomeOnClick = useCallback(() => {
    setIsRentalIncomeDialogOpen(true);
    setIsAppreciationDialogOpen(false);
  }, []);
  const handleRentalIncomeDialogOnClose = useCallback(() => setIsRentalIncomeDialogOpen(false), []);

  return (
    <>
      <AppreciationDialog
        accountId={accountId}
        onClose={handleAppreciationDialogOnClose}
        open={isAppreciationDialogOpen}
      />
      <RentalIncomeDialog
        accountId={accountId}
        onClose={handleRentalIncomeDialogOnClose}
        open={isRentalIncomeDialogOpen}
      />
      <Grid className={className} container spacing={{ base: 2, lg: 6 }}>
        <Grid item xs={12} lg={4}>
          <PortfolioCard
            className="balance-card"
            data-e2e="balance-stat"
            subText={<PortfolioAppreciation appreciation={totalAppreciation} initialInvestment={initialInvestment} />}
            title={<FormattedMessage id="balance" defaultMessage="Balance" />}
            value={totalValue}
            sx={{ width: '100%', height: '100% ' }}
          />
        </Grid>
        <Grid item xs={6} lg={4}>
          <PortfolioCard
            title={<FormattedMessage id="appreciation-returns" defaultMessage="Appreciation Returns" />}
            onClick={handleAppreciationOnClick}
            value={totalAppreciation}
            sx={{ width: '100%', height: '100% ' }}
          />
        </Grid>
        <Grid item xs={6} lg={4}>
          <PortfolioCard
            subText={<RentalIncomePercentText rentalIncomePercent={blendedAnnualDividendPercent} />}
            title={<FormattedMessage id="rental-income" defaultMessage="Rental Income" />}
            onClick={handleRentalIncomeOnClick}
            value={totalDividendsPaid}
            sx={{ width: '100%', height: '100% ' }}
          />
        </Grid>
      </Grid>
    </>
  );
};
